import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { longHourInterviewApi } from "../../services/longHourInterviewApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";

const ESTABLISHMENT_STATUSES = ["NOT_ESTABLISHED", "REVIEW_REQUIRED", "ESTABLISHED"];
const EMPTY_RULE = { rule_name: "", threshold_hours: "", period_type: "MONTHLY", applies_to: "ALL_EMPLOYEES" };

export default function LongHourInterviewPoliciesPage() {
  const { t } = useLocalization();
  const { canViewLongHourInterview, canManageLongHourInterview } = usePermissions();
  const [policy, setPolicy] = useState(null);
  const [note, setNote] = useState("");
  const [rules, setRules] = useState([]);
  const [newRule, setNewRule] = useState(EMPTY_RULE);
  const [busy, setBusy] = useState(false);

  const load = () => {
    longHourInterviewApi.getPolicy().then((r) => { setPolicy(r.data); setNote(r.data.establishment_note || ""); }).catch(() => {});
    longHourInterviewApi.listThresholdRules().then((r) => setRules(r.data)).catch(() => {});
  };
  useEffect(() => { if (canViewLongHourInterview) load(); }, [canViewLongHourInterview]);

  if (!canViewLongHourInterview) return <p className="text-sm text-gray-500" data-testid="long-hour-access-denied">{t("common.accessDenied")}</p>;

  const setStatus = async (status) => {
    setBusy(true);
    try { await longHourInterviewApi.updatePolicy({ establishment_status: status, note }); toast.success(t("mgmtLongHourInterview.policyUpdated")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const createRule = async () => {
    if (!newRule.rule_name || !newRule.threshold_hours) return;
    setBusy(true);
    try {
      await longHourInterviewApi.createThresholdRule({ ...newRule, threshold_hours: Number(newRule.threshold_hours) });
      toast.success(t("mgmtLongHourInterview.thresholdRuleCreated"));
      setNewRule(EMPTY_RULE);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const deactivateRule = async (ruleId) => {
    setBusy(true);
    try { await longHourInterviewApi.deactivateThresholdRule(ruleId); toast.success(t("mgmtLongHourInterview.ruleDeactivated")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="long-hour-policies-page">
      <PageHelpBanner pageKey="long-hour-policies" title={t("pageHelp.defaultTitle")}>
        <p>{t("mgmtLongHourInterview.policiesHelp")}</p>
      </PageHelpBanner>
      <div>
        <Link to="/management/compliance/long-hour-interviews" className="text-xs font-semibold text-blue-600 hover:underline" data-testid="long-hour-back-link">&larr; {t("mgmtLongHourInterview.backToDashboard")}</Link>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 mt-1">{t("mgmtLongHourInterview.policyThresholdRules")}</h2>
      </div>

      {policy && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3" data-testid="long-hour-policy-config-panel">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-sm font-semibold text-gray-700">{t("mgmtLongHourInterview.policyEstablishmentStatus")}</h3>
            <StatusBadge status={policy.establishment_status} />
          </div>
          {canManageLongHourInterview && (
            <div className="flex flex-wrap gap-2 items-end">
              <textarea data-testid="long-hour-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="flex-1 min-w-[240px] border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder={t("mgmtLongHourInterview.noteApplicability")} />
              {ESTABLISHMENT_STATUSES.map((s) => (
                <button key={s} data-testid={`long-hour-set-status-${s}`} disabled={busy || s === policy.establishment_status} onClick={() => setStatus(s)}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold border disabled:opacity-40 ${s === policy.establishment_status ? "bg-gray-900 text-white border-gray-900" : "border-gray-200 hover:bg-gray-50"}`}>
                  {s.replace(/_/g, " ")}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {canManageLongHourInterview && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-wrap gap-2 items-end" data-testid="long-hour-new-rule-form">
          <input data-testid="long-hour-rule-name-input" value={newRule.rule_name} onChange={(e) => setNewRule((r) => ({ ...r, rule_name: e.target.value }))} placeholder={t("mgmtLongHourInterview.ruleNamePlaceholder")} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          <input data-testid="long-hour-rule-hours-input" type="number" value={newRule.threshold_hours} onChange={(e) => setNewRule((r) => ({ ...r, threshold_hours: e.target.value }))} placeholder={t("mgmtLongHourInterview.thresholdHoursPlaceholder")} className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-32" />
          <select data-testid="long-hour-rule-period-select" value={newRule.period_type} onChange={(e) => setNewRule((r) => ({ ...r, period_type: e.target.value }))} className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
            <option value="MONTHLY">MONTHLY</option>
          </select>
          <button data-testid="long-hour-create-rule-btn" disabled={busy || !newRule.rule_name || !newRule.threshold_hours} onClick={createRule} className="px-4 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">{t("mgmtLongHourInterview.addRule")}</button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr><th className="px-4 py-3 font-medium">{t("mgmtLongHourInterview.colRule")}</th><th className="px-4 py-3 font-medium">{t("mgmtLongHourInterview.threshold")}</th><th className="px-4 py-3 font-medium">{t("mgmtLongHourInterview.appliesTo")}</th><th className="px-4 py-3 font-medium">{t("common.status")}</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id} className="border-t border-gray-100" data-testid={`long-hour-rule-row-${r.id}`}>
                <td className="px-4 py-2 text-gray-700">{r.rule_name}</td>
                <td className="px-4 py-2 text-gray-500 text-xs">{r.threshold_hours}h / {r.period_type}</td>
                <td className="px-4 py-2 text-gray-500 text-xs">{r.applies_to}</td>
                <td className="px-4 py-2"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-2">
                  {canManageLongHourInterview && r.status === "ACTIVE" && (
                    <button data-testid={`long-hour-deactivate-rule-${r.id}`} disabled={busy} onClick={() => deactivateRule(r.id)} className="text-xs font-semibold text-red-500 hover:underline">{t("mgmtHealthCommittee.deactivate")}</button>
                  )}
                </td>
              </tr>
            ))}
            {rules.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500" data-testid="no-long-hour-rules">{t("mgmtLongHourInterview.noRulesConfigured")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
